import isNumber from "lodash/isNumber";
import unionWith from "lodash/unionWith";
import isPlainObject from "lodash/isPlainObject";
import queryString from "query-string";

import Context from "shared/util/Context";
import OkC from "shared/util/OkcApi";
import { isDesktop } from "shared/util/EnvironmentHelpers";

// actions from other ducks that should cause quickmatch to refresh
import { SAVE_FIELD_DATA_SUCCESS } from "ducks/what_i_want";
import { SUBMIT_ANSWER_SUCCESS } from "ducks/questions";
import { SET_PREFERENCES_SUCCESS } from "ducks/global_preferences";

import { STACKS, STACKS_VIEWS, FETCH_STATUSES } from "apps/quickmatch/util/constants";
import { prepUserData } from "apps/quickmatch/util/helpers";
import getStack from "apps/quickmatch/util/getStack";
import {
	reachedDailyLimitAnalytics,
	loadStackAnalytics,
} from "apps/quickmatch/util/analytics";

const MAX_NUM_FOR_QUEUE = 20;
let PROFILE_FIELDS = "userinfo,percentages,thumbs,likes,last_contacts,last_login,essays,details,connection_compatibilities,online";
const ME_FIELDS = "thumbs";

if (isDesktop) {
	PROFILE_FIELDS = `${PROFILE_FIELDS},similar_users`;
}


// helpers
// ----------------------------------------------------------------------------

function getUniqueIdForCard(card) {
	switch (card.cardType) {
		case "user": {
			return card.userid;
		}
		case "firstPartyAds":
		case "promos": {
			return card.name;
		}
		default: {
			return card.userid;
		}
	}
}

function apiToClientCard(apiCard) {
	return {
		...apiCard,
		data: {
			...apiCard.data,
			id: getUniqueIdForCard(apiCard.data),
		},
	};
}

export function addDirtyStatuses(currentStatuses, dirtyStacks) {
	if (!isPlainObject(currentStatuses) || !Array.isArray(dirtyStacks)) {
		return currentStatuses;
	}

	return Object.keys(currentStatuses).reduce((acc, id) => {
		return {
			...acc,
			[id]: {
				...currentStatuses[id],
				isDirty: dirtyStacks.includes(id) ? true : currentStatuses[id].isDirty,
			},
		};
	}, currentStatuses);
}

function addUniqueUsersToList(currentUsers, newUsers) {
	const res = [...currentUsers];
	const currentUserids = res.map((i) => {
		return prepUserData(i).userid;
	});

	newUsers.forEach((i) => {
		const userid = prepUserData(i).userid;

		if (!currentUserids.includes(userid)) {
			res.push(i);
		}
	});

	return res;
}


// action types
// ----------------------------------------------------------------------------

export const MOUNT = "QUICKMATCH.MOUNT";
export const DISMISS_MUTUAL_MATCH = "QUICKMATCH.DISMISS_MUTUAL_MATCH";
export const SHOW_ERROR = "QUICKMATCH.SHOW_ERROR";
export const HAS_ONBOARDED_TAPPY = "QUICKMATCH.HAS_ONBOARDED_TAPPY";
export const HAS_ONBOARDED_SWIPEY = "QUICKMATCH.HAS_ONBOARDED_SWIPEY";
export const GET_DOUBLETAKE_QUEUE_START = "QUICKMATCH.GET_DOUBLETAKE_QUEUE_START";
export const GET_DOUBLETAKE_QUEUE_COMPLETE = "QUICKMATCH.GET_DOUBLETAKE_QUEUE_COMPLETE";
export const VOTE_START = "QUICKMATCH.VOTE_START";
export const VOTE_COMPLETE = "QUICKMATCH.VOTE_COMPLETE";
export const DISMISS_CARD = "QUICKMATCH.DISMISS_CARD";
export const GET_ME_START = "QUICKMATCH.GET_ME_START";
export const GET_ME_COMPLETE = "QUICKMATCH.GET_ME_COMPLETE";

export const SET_PENDING_VOTE = "QUICKMATCH.SET_PENDING_VOTE";
export const CLEAR_PENDING_VOTE = "QUICKMATCH.CLEAR_PENDING_VOTE";

export const CLOSE_LIKES_CAP_MODAL = "QUICKMATCH.CLOSE_LIKES_CAP_MODAL";
export const INCREMENT_LIKES_CAP_RATE_CARD_VIEW_COUNT = "QUICKMATCH.INCREMENT_LIKES_CAP_RATE_CARD_VIEW_COUNT";
export const ADD_TOP_OF_QUEUE = "QUICKMATCH.ADD_TOP_OF_QUEUE";

export const ADD_STACKS_TO_REDUX = "QUICKMATCH.ADD_STACKS_TO_REDUX";
export const ADD_STACK_TO_REDUX = "QUICKMATCH.ADD_STACK_TO_REDUX";
export const LOAD_USER_GUIDES = "QUICKMATCH.LOAD_USER_GUIDES";
export const SET_USER_GUIDE_SEEN = "QUICKMATCH.SET_USER_GUIDE_SEEN";
export const SET_ACTIVE_STACK = "QUICKMATCH.SET_ACTIVE_STACK";
export const PURCHASED_STACK = "QUICKMATCH.PURCHASED_STACK";
export const CHANGE_STACKS_VIEW = "QUICKMATCH.CHANGE_STACKS_VIEW";
export const MARK_STACK_AS_LOADING = "QUICKMATCH.MARK_STACK_AS_LOADING";
export const SET_IS_STACKS_TEST_GROUP = "QUICKMATCH.SET_IS_STACKS_TEST_GROUP";

export const REMOVE_USER_FROM_QUEUES = "QUICKMATCH.REMOVE_USER_FROM_QUEUES";
export const REFRESH_ON_NEXT_LOAD = "QUICKMATCH.REFRESH_ON_NEXT_LOAD";
export const SET_EMPTY_STACK = "QUICKMATCH.SET_EMPTY_STACK";
export const SET_LAST_USER_DATA = "QUICKMATCH.SET_LAST_USER_DATA";

export const SHOW_REGRET_SWIPE_ALERT = "QUICKMATCH.SHOW_REGRET_SWIPE_ALERT";


// initial state
// ----------------------------------------------------------------------------

const minimalStackData = {
	data: [],
};

const minimalStackStatuses = {
	fetchStatus: FETCH_STATUSES.loading,
	isEmpty: false,
	isDirty: false,
};

export const INITIAL_STATE = {
	isMounted: false,
	me: {},
	recentVote: {
		id: null,
		liked: false,
	},
	mutualMatch: null,
	activeRequestCount: 0,
	isFetchingMe: false,
	isVoting: false,
	likesRemaining: 50,
	isLikesCapModalOpen: false,
	likesCapResetTime: null,
	likesCapRateCardViewCount: 0,
	voteCount: 0,
	passCount: 0,
	stacksHasLoaded: false,
	stacks: Object.keys(STACKS).reduce((acc, stackId) => ({
		...acc,
		[stackId]: { ...minimalStackData },
	}), {}),
	statuses: Object.keys(STACKS).reduce((acc, stackId) => ({
		...acc,
		[stackId]: { ...minimalStackStatuses },
	}), {}),
	voteCounts: Object.keys(STACKS).reduce((acc, stackId) => ({
		...acc,
		[stackId]: 0,
	}), {}),
	isStacksTestGroup: false,
	stackOrder: [],
	emptyStackIds: [],
	userGuides: {},
	activeStackId: STACKS.JUST_FOR_YOU,
	lastUserData: null,
	stacksView: STACKS_VIEWS.SWIPE,

	// Staff only stuff
	forceMutualMatch: false,
	forceError: false,

	hasOnboardedTappy: true,
	hasOnboardedSwipey: true,
	purchasedStackId: null,
	isRegretSwipeOpen: false,
};

const getInitialState = () => {
	const { stack } = queryString.parse(window.location.search);

	const activeStackId = stack && STACKS[stack] ? stack : STACKS.JUST_FOR_YOU;

	return {
		...INITIAL_STATE,
		activeStackId,
	};
};


// reducer
// ----------------------------------------------------------------------------

export function reducer(state = getInitialState(), action) {
	switch (action.type) {
		case MOUNT: {
			return {
				...state,
				isMounted: true,
			};
		}

		case DISMISS_MUTUAL_MATCH: {
			return {
				...state,
				mutualMatch: null,
			};
		}

		case SHOW_ERROR: {
			return {
				...state,
				error: { message: action.message },
			};
		}

		case HAS_ONBOARDED_TAPPY: {
			const hasOnboardedTappy = typeof (action.payload || {}).hasOnboardedTappy === "boolean"
				? action.payload.hasOnboardedTappy
				: true;

			return {
				...state,
				hasOnboardedTappy,
			};
		}

		case HAS_ONBOARDED_SWIPEY: {
			const hasOnboardedSwipey = typeof (action.payload || {}).hasOnboardedSwipey === "boolean"
				? action.payload.hasOnboardedSwipey
				: true;

			return {
				...state,
				hasOnboardedSwipey,
			};
		}

		case GET_DOUBLETAKE_QUEUE_START: {
			return {
				...state,
				statuses: {
					...state.statuses,
					[STACKS.JUST_FOR_YOU]: {
						...state.statuses[STACKS.JUST_FOR_YOU],
						fetchStatus: FETCH_STATUSES.loading,
					},
				},
			};
		}

		case GET_DOUBLETAKE_QUEUE_COMPLETE: {
			// get the id from a pubexpress- or okapi-formatted user
			const getId = (el) => {
				return el?.data?.id || el?.match?.user?.id || null;
			};

			// need to figure out how to dedupe this when it has multiple types
			const newQueue = unionWith(state.stacks[STACKS.JUST_FOR_YOU].data, action.queue, (a, b) => (
				getId(a) === getId(b)
			)).filter((i) => {
				return i.cardType === "user" || i.__typename === "StackMatch";
			});

			const likesRemaining = isNumber(action.likesRemaining)
				? action.likesRemaining
				: state.likesRemaining;
			const likesCapResetTime = action.likesCapResetTime;
			const likesCapRateCardViewCount = action.likesCapRateCardViewCount || 0;
			const shouldTrackHitLikesCap = typeof action.shouldTrackHitLikesCap === "boolean"
				? action.shouldTrackHitLikesCap
				: state.shouldTrackHitLikesCap;
			const isEmpty = !newQueue.length;

			return {
				...state,
				stacks: {
					...state.stacks,
					[STACKS.JUST_FOR_YOU]: {
						id: STACKS.JUST_FOR_YOU,
						data: newQueue,
					},
				},
				statuses: {
					...state.statuses,
					[STACKS.JUST_FOR_YOU]: {
						...state.statuses[STACKS.JUST_FOR_YOU],
						isEmpty,
						fetchStatus: FETCH_STATUSES.complete,
					},
				},
				likesRemaining,
				likesCapResetTime,
				shouldTrackHitLikesCap,
				likesCapRateCardViewCount,
				hasOnboardedTappy: typeof action.hasOnboardedTappy === "boolean"
					? action.hasOnboardedTappy
					: state.hasOnboardedTappy,
				hasOnboardedSwipey: typeof action.hasOnboardedSwipey === "boolean"
					? action.hasOnboardedSwipey
					: state.hasOnboardedSwipey,
			};
		}

		case VOTE_START: {
			const { targetUserId, liked } = action.payload;

			// only block likes
			if (!state.likesRemaining && liked) {
				return {
					...state,
					isLikesCapModalOpen: true,
				};
			}

			const dirtyStacks = [];

			// remove the voted user from all stacks
			const stacks = Object.keys(state.stacks).reduce((agg, id) => ({
				...agg,
				[id]: {
					...state.stacks[id],
					data: state.stacks[id].data.filter((user) => {
						const foundUser = prepUserData(user).userid === targetUserId;

						if (foundUser && id !== state.activeStackId) {
							dirtyStacks.push(id);
						}

						return !foundUser;
					}),
				},
			}), {});

			return {
				...state,
				isVoting: true,
				recentVote: {
					id: targetUserId,
					liked,
				},
				stacks,
				statuses: addDirtyStatuses(state.statuses, dirtyStacks),

				// increment the vote count for the active stack
				voteCounts: {
					...state.voteCounts,
					[state.activeStackId]: state.voteCounts[state.activeStackId] + 1,
				},

				likesRemaining: liked ? state.likesRemaining - 1 : state.likesRemaining,
				activeRequestCount: state.activeRequestCount + 1,
				voteCount: state.voteCount + 1,
				passCount: liked ? state.passCount : state.passCount + 1,
			};
		}

		case VOTE_COMPLETE: {
			const likesRemaining = isNumber(action.likesRemaining)
				? action.likesRemaining
				: state.likesRemaining;
			const likesCapResetTime = action.likesCapResetTime;
			const shouldTrackHitLikesCap = !!action.shouldTrackHitLikesCap;

			const mutualMatch = action.mutualMatch
				? action.mutualMatch
				: state.mutualMatch;

			const isEmpty = !state.stacks[state.activeStackId].data.length;

			// analytics for when a stack becomes empty
			if (!state.statuses[state.activeStackId].isEmpty && isEmpty) {
				reachedDailyLimitAnalytics(state.activeStackId);
			}

			return {
				...state,
				statuses: {
					...state.statuses,
					[state.activeStackId]: {
						...state.statuses[state.activeStackId],
						isEmpty,
					},
				},
				isVoting: false,
				likesRemaining,
				likesCapResetTime,
				shouldTrackHitLikesCap,
				mutualMatch,
				activeRequestCount: state.activeRequestCount - 1,
			};
		}

		case DISMISS_CARD: {
			const { targetUserId, liked } = action.payload;

			return {
				...state,
				isVoting: true,
				recentVote: {
					id: targetUserId,
					liked,
				},
				stacks: Object.keys(state.stacks).reduce((agg, id) => ({
					...agg,
					[id]: {
						...state.stacks[id],
						data: state.stacks[id].data.filter((user) => {
							return prepUserData(user).userid !== targetUserId;
						}),
					},
				}), {}),
				likesRemaining: liked ? state.likesRemaining - 1 : state.likesRemaining,
			};
		}

		case GET_ME_START: {
			return {
				...state,
				isFetchingMe: true,
			};
		}

		case GET_ME_COMPLETE: {
			const me = action.me;
			return {
				...state,
				isFetchingMe: false,
				me,
			};
		}

		case SHOW_REGRET_SWIPE_ALERT: {
			const { isOpen } = action.payload;

			return {
				...state,
				isRegretSwipeOpen: isOpen,
			};
		}

		case CLOSE_LIKES_CAP_MODAL: {
			return {
				...state,
				isLikesCapModalOpen: false,
			};
		}

		case INCREMENT_LIKES_CAP_RATE_CARD_VIEW_COUNT: {
			const {
				payload: { likesCapRateCardViewCount },
			} = action;

			return {
				...state,
				likesCapRateCardViewCount,
			};
		}

		case ADD_TOP_OF_QUEUE: {
			const {
				payload: { user },
			} = action;
			const newQueue = [user, ...state.stacks[state.activeStackId].data];

			return {
				...state,
				stacks: {
					...state.stacks,
					[state.activeStackId]: {
						...state.stacks[state.activeStackId],
						data: newQueue,
					},
				},
			};
		}

		case SET_PENDING_VOTE: {
			const { userid, isLike } = action.payload;

			return {
				...state,
				pendingVote: {
					userid,
					isLike,
				},
			};
		}

		case CLEAR_PENDING_VOTE: {
			return {
				...state,
				pendingVote: undefined,
			};
		}

		case ADD_STACKS_TO_REDUX: {
			if (!action.payload || !action.payload.stacks || !action.payload.stacks.length) {
				return state;
			}

			const stackOrder = state.stackOrder
				.concat(action.payload.stacks.map((stack) => stack.id));
			const emptyStackIds = action.payload.stacks
				.filter((stack) => !stack.data.length)
				.map((stack) => stack.id);

			return {
				...state,
				stacksHasLoaded: true,
				stacks: action.payload.stacks.reduce((agg, stack) => ({
					...agg,
					[stack.id]: {
						...stack,
						data: addUniqueUsersToList(state.stacks[stack.id].data, stack.data),
					},
				}), state.stacks),
				statuses: action.payload.stacks.reduce((agg, stack) => ({
					...agg,
					[stack.id]: {
						isEmpty: !stack.data.length,
						isDirty: stack.id !== STACKS.JUST_FOR_YOU && !!stack.data.length,
						fetchStatus: FETCH_STATUSES.complete,
					},
				}), state.statuses),
				stackOrder,
				emptyStackIds,
			};
		}

		case ADD_STACK_TO_REDUX: {
			const { stackId, data, status, expireTime } = action.payload;

			if (
				!stackId
				|| !data
			) {
				return state;
			}

			const newStatus = status || state.stacks[stackId].status;
			const newExpireTime = expireTime || state.stacks[stackId].expireTime;
			const newStack = {
				...state.stacks[stackId],
				data: addUniqueUsersToList(state.stacks[stackId].data, data),
				status: newStatus,
				expireTime: newExpireTime,
			};

			loadStackAnalytics(newStack, state.stackOrder);

			return {
				...state,
				stacks: {
					...state.stacks,
					[stackId]: newStack,
				},
				statuses: {
					...state.statuses,
					[stackId]: {
						...state.statuses[stackId],
						isEmpty: !newStack.data.length,
						isDirty: false,
						fetchStatus: FETCH_STATUSES.complete,
					},
				},
			};
		}

		case MARK_STACK_AS_LOADING: {
			const { stackId } = action.payload;

			if (!stackId || !STACKS[stackId]) {
				return state;
			}

			return {
				...state,
				statuses: {
					...state.statuses,
					[stackId]: {
						...state.statuses[stackId],
						fetchStatus: FETCH_STATUSES.loading,
					},
				},
			};
		}

		case LOAD_USER_GUIDES: {
			return {
				...state,
				userGuides: action.payload.userGuides,
			};
		}

		case SET_USER_GUIDE_SEEN: {
			return {
				...state,
				userGuides: {
					...state.userGuides,
					[action.payload.stackId]: true,
				},
			};
		}

		case SET_ACTIVE_STACK: {
			const newStackId = action.payload.id;

			if (!newStackId || !state.stacks[newStackId]) {
				return state;
			}

			// if the newly active stack has a dirty status, mark it as fetching
			const statuses = state.statuses[newStackId].isDirty
				? {
					...state.statuses,
					[newStackId]: {
						...state.statuses[newStackId],
						fetchStatus: FETCH_STATUSES.loading,
					},
				}
				: state.statuses;

			return {
				...state,
				activeStackId: newStackId,
				statuses,
				purchasedStackId: null,
			};
		}

		case REMOVE_USER_FROM_QUEUES: {
			if (!action.payload || !action.payload.userid) {
				return state;
			}

			const { userid, liked } = action.payload;
			const voteCounts = { ...state.voteCounts };
			const dirtyStacks = [];

			// remove the given user from all stacks
			const newStacks = Object.keys(state.stacks).reduce((acc, id) => {
				return {
					...acc,
					[id]: {
						...state.stacks[id],
						data: state.stacks[id].data.filter((i) => {
							const user = prepUserData(i);

							if (user.userid === userid) {
								// if you navigated to a profile from the stack and voted on
								// that profile, the stack is not actually dirty, it is just
								// being voted through normally
								if (id !== state.activeStackId) {
									dirtyStacks.push(id);
								}

								return false;
							}

							return true;
						}),
					},
				};
			}, {});

			if (state.lastUserData) {
				voteCounts[state.activeStackId] += 1;
			}

			const statuses = addDirtyStatuses(state.statuses, dirtyStacks);

			// if the last user was removed from the active stack, we know we are at
			// the end of the stack, so mark it as empty
			if (!newStacks[state.activeStackId].data.length) {
				statuses[state.activeStackId] = {
					...statuses[state.activeStackId],
					isEmpty: true,
				};
			}

			return {
				...state,
				stacks: newStacks,
				statuses,
				voteCounts,
				likesRemaining: liked ? state.likesRemaining - 1 : state.likesRemaining,
			};
		}

		case SET_PREFERENCES_SUCCESS:
		case SUBMIT_ANSWER_SUCCESS:
		case SAVE_FIELD_DATA_SUCCESS:
		case REFRESH_ON_NEXT_LOAD: {
			return {
				...state,
				isMounted: false,
				stacksHasLoaded: false,
				activeStackId: STACKS.JUST_FOR_YOU,
				stacks: { ...INITIAL_STATE.stacks },
				statuses: { ...INITIAL_STATE.statuses },
				voteCounts: { ...INITIAL_STATE.voteCounts },
				stackOrder: [...INITIAL_STATE.stackOrder],
				emptyStackIds: [...INITIAL_STATE.emptyStackIds],
			};
		}

		case SET_EMPTY_STACK: {
			if (!action.payload) {
				return state;
			}

			const { stackId } = action.payload;
			const emptyStackIds = state.emptyStackIds;

			if (
				!stackId
				|| !state.stackOrder.includes(stackId)
				|| emptyStackIds.includes(stackId)
			) {
				return state;
			}

			emptyStackIds.push(stackId);

			return {
				...state,
				stackOrder: state.stackOrder.filter((id) => id !== stackId).concat([stackId]),
				emptyStackIds,
			};
		}

		case SET_LAST_USER_DATA: {
			return {
				...state,
				lastUserData: action.payload.userData,
			};
		}

		case PURCHASED_STACK: {
			const { stackId } = action.payload;
			return {
				...state,
				purchasedStackId: stackId,
			};
		}

		case CHANGE_STACKS_VIEW: {
			const { stacksView } = action.payload;

			if (!stacksView || !STACKS_VIEWS[stacksView]) {
				return state;
			}

			return {
				...state,
				stacksView,
			};
		}

		case SET_IS_STACKS_TEST_GROUP: {
			const { isStacksTestGroup } = action.payload;

			return {
				...state,
				isStacksTestGroup,
			};
		}

		default:
			return state;
	}
}


// action creators
// ----------------------------------------------------------------------------

export function closeLikesCapModal() {
	return {
		type: CLOSE_LIKES_CAP_MODAL,
	};
}

export function setHasOnboardedTappy(hasOnboardedTappy) {
	return {
		type: HAS_ONBOARDED_TAPPY,
		payload: {
			hasOnboardedTappy,
		},
	};
}

export function onOnboardTappy() {
	return (dispatch) => {
		dispatch({
			type: HAS_ONBOARDED_TAPPY,
		});
	};
}

export function setHasOnboardedSwipey(hasOnboardedSwipey) {
	return {
		type: HAS_ONBOARDED_SWIPEY,
		payload: {
			hasOnboardedSwipey,
		},
	};
}

export function onOnboardedSwipey() {
	return (dispatch) => {
		dispatch({
			type: HAS_ONBOARDED_SWIPEY,
		});
	};
}

/**
 * Dismiss the mutual match that was just made.
 * @returns {object}
 */
export function dismissMutualMatch() {
	return { type: DISMISS_MUTUAL_MATCH };
}

export function getMe() {
	return (dispatch) => {
		dispatch({ type: GET_ME_START });

		OkC.get(`profile/${Context.get("userid")}/fields`, {
			version: 1,
			data: {
				fields: ME_FIELDS,
			},
		}).then((me) => {
			dispatch({
				type: GET_ME_COMPLETE,
				me,
			});
		}).catch(() => {
			dispatch({
				type: GET_ME_COMPLETE,
				me: {},
			});
		});
	};
}

export function getDoubletakeQueue() {
	return (dispatch, getStore) => {
		const store = getStore().quickmatch;
		const { isMounted, stacks, activeStackId, isStacksTestGroup } = store;
		const fetchStatus = store.statuses[STACKS.JUST_FOR_YOU].fetchStatus;
		const isFetchingQueue = fetchStatus === FETCH_STATUSES.loading;

		if (isMounted && isFetchingQueue) {
			return;
		}

		dispatch({ type: GET_DOUBLETAKE_QUEUE_START });

		const excludes = stacks[activeStackId].data.map((data) => {
			return prepUserData(data).userid;
		});
		const numToFetch = Math.max(0, MAX_NUM_FOR_QUEUE - excludes.length);

		if (isStacksTestGroup) {
			getStack(
				Context.get("userid"),
				STACKS.JUST_FOR_YOU,
				stacks[STACKS.JUST_FOR_YOU].data.map((stackMatch) => stackMatch?.match?.user?.id)
					.filter((id) => !!id),
			).then((res) => {
				// get the queue of users, filtering out nulls; this allows us to not
				// break the interface in the case of a new, unhandled union type in
				// the results
				const queue = res.user?.stack?.data.filter((i) => !!i);
				const likesRemaining = res.user?.likesCap?.likesRemaining;
				const likesCapResetTime = res.user?.likesCap?.resetTime;
				const likesCapRateCardViewCount = res.user?.likesCap?.viewCount;

				dispatch({
					type: GET_DOUBLETAKE_QUEUE_COMPLETE,
					queue,
					likesRemaining,
					likesCapResetTime,
					likesCapRateCardViewCount,
				});
			}).catch(() => {
				dispatch({
					type: GET_DOUBLETAKE_QUEUE_COMPLETE,
					queue: [],
				});
			});
		} else {
			OkC.post("/locals", {
				version: 2,
				data: {
					size: numToFetch,
					excludes,
					extras: true,
					fields: PROFILE_FIELDS,
				},
			}).then((res) => {
				const resCards = (res.cards || [])
					.filter(({ cardType }) => (
						cardType === "user"

						// Disable these other types, for now.
						// || cardType === "firstPartyAds"
						// || cardType === "promos"
					))
					.map(apiToClientCard);

				const extras = res.extras || {};

				dispatch({
					type: GET_DOUBLETAKE_QUEUE_COMPLETE,
					queue: resCards,
					likesRemaining: +extras.likesRemaining,
					likesCapResetTime: extras.likesCapResetTime,
					likesCapRateCardViewCount: extras.likesCapRateCardViewCount,
					shouldTrackHitLikesCap: extras.shouldTrackHitLikesCap,

					// If the server explicitly tells us onboarding status, then use it.
					// If the server doesn't send anything back, let's not assume they need to see this thing.
					hasOnboardedTappy: typeof extras.hasOnboardedTappy === "boolean" ? extras.hasOnboardedTappy : true,
					hasOnboardedSwipey: typeof extras.hasOnboardedSwipey === "boolean" ? extras.hasOnboardedSwipey : true,
				});
			}).catch(() => {
				// Still gotta do this.
				dispatch({
					type: GET_DOUBLETAKE_QUEUE_COMPLETE,
					queue: [],
				});
			});
		}
	};
}

export function dismissCard(userid, liked) {
	return {
		type: DISMISS_CARD,
		payload: {
			targetUserId: userid,
			liked,
		},
	};
}

export function setRegretSwipeVisibility(isOpen) {
	return {
		type: SHOW_REGRET_SWIPE_ALERT,
		payload: {
			isOpen,
		},
	};
}

export function vote({ liked, userid, userData, cardType, inputMethod }) {
	return (dispatch, getStore) => {
		const store = getStore().quickmatch;
		const { activeStackId, stacks } = store;
		const quickmatchQueue = [...stacks[store.activeStackId].data];
		const queueLengthAfterVoting = quickmatchQueue.length - 1;

		if (
			activeStackId === STACKS.JUST_FOR_YOU
			&& queueLengthAfterVoting <= MAX_NUM_FOR_QUEUE / 2
		) {
			dispatch(getDoubletakeQueue());
		}

		dispatch({
			type: VOTE_START,
			payload: {
				targetUserId: userid,
				liked,
			},
		});

		OkC.post("locals/vote", {
			data: {
				votes: [{
					cardType,
					id: userid,
					voteValue: liked ? "yes" : "no",
					subjectId: userid,
					user_data: userData,
				}],
			},
		}).then(({ likesRemaining, likesCapResetTime, shouldTrackHitLikesCap, user }) => {
			// UID is only returned if it's a mutual match. locals/vote is a wild place.
			const mutualLikeTargetId = user && user[0] && user[0].uid;
			const matchedUser = quickmatchQueue.find((u) => {
				const userInfo = prepUserData(u);
				return userInfo.userid === mutualLikeTargetId;
			});

			dispatch({
				type: VOTE_COMPLETE,
				likesRemaining,
				likesCapResetTime,
				shouldTrackHitLikesCap,
				mutualMatch: mutualLikeTargetId && likesRemaining ? matchedUser : null,

				// for analytics
				liked,
				cardType,
				inputMethod,
				targetUserId: userid,
			});

			// eslint-disable-next-line camelcase
			const isTargetUserInterested = quickmatchQueue.some(({ data: userInQueueData }) =>
				userInQueueData?.userid === userid && userInQueueData?.interested);
			const cannotViewVotesAndPassedOnInterestedUser = !Context.hasPremium("VIEW_VOTES") && isTargetUserInterested;

			if (cannotViewVotesAndPassedOnInterestedUser) {
				dispatch(setRegretSwipeVisibility(true));
			}
		}).catch((err) => {
			console.error("quickmatch vote error:", err);
			dispatch({ type: VOTE_COMPLETE });
		});
	};
}

function _incrementLikesCapRateCardViewCount(count) {
	return {
		type: INCREMENT_LIKES_CAP_RATE_CARD_VIEW_COUNT,
		payload: {
			likesCapRateCardViewCount: count,
		},
	};
}

export function incrementLikesCapModalViewCount() {
	return (dispatch, getStore) => {
		const { likesCapRateCardViewCount } = getStore().quickmatch;
		dispatch(_incrementLikesCapRateCardViewCount(likesCapRateCardViewCount + 1));

		OkC.post("/rate_card/view", {
			version: 1,
			data: {
				promo_id: "alist.likescappaywall",
			},
		})
			.then(({ view_count: viewCount }) => {
				dispatch(_incrementLikesCapRateCardViewCount(viewCount));
			});
	};
}

export function addToTopOfQueue(user) {
	return {
		type: ADD_TOP_OF_QUEUE,
		payload: { user },
	};
}

export function purchasedStack(stackId) {
	return {
		type: PURCHASED_STACK,
		payload: { stackId },
	};
}

export function setPendingVote(userid, isLike) {
	return {
		type: SET_PENDING_VOTE,
		payload: {
			userid,
			isLike,
		},
	};
}

export function clearPendingVote() {
	return {
		type: CLEAR_PENDING_VOTE,
	};
}

export function addStacksToRedux(stacks) {
	return {
		type: ADD_STACKS_TO_REDUX,
		payload: {
			stacks,
		},
	};
}

export function addStackToRedux({ stackId, data, status, expireTime } = {}) {
	return {
		type: ADD_STACK_TO_REDUX,
		payload: {
			stackId,
			data,
			status,
			expireTime,
		},
	};
}

export function loadUserGuides(userGuides) {
	return {
		type: LOAD_USER_GUIDES,
		payload: {
			userGuides,
		},
	};
}

export function setUserGuideSeen(stackId) {
	return {
		type: SET_USER_GUIDE_SEEN,
		payload: {
			stackId,
		},
	};
}

export function setActiveStack(id) {
	return {
		type: SET_ACTIVE_STACK,
		payload: {
			id,
		},
	};
}

export function mount() {
	return { type: MOUNT };
}

export function removeUserFromQueues(userid, { liked } = {}) {
	return {
		type: REMOVE_USER_FROM_QUEUES,
		payload: {
			userid,
			liked,
		},
	};
}

export function setEmptyStack(stackId) {
	return {
		type: SET_EMPTY_STACK,
		payload: {
			stackId,
		},
	};
}

export function setLastUserData(userData) {
	return {
		type: SET_LAST_USER_DATA,
		payload: {
			userData,
		},
	};
}

export function changeStacksView(stacksView) {
	return {
		type: CHANGE_STACKS_VIEW,
		payload: {
			stacksView,
		},
	};
}

export function markStackAsLoading(stackId) {
	return {
		type: MARK_STACK_AS_LOADING,
		payload: {
			stackId,
		},
	};
}

export function setIsStacksTestGroup(isStacksTestGroup) {
	return {
		type: SET_IS_STACKS_TEST_GROUP,
		payload: {
			isStacksTestGroup,
		},
	};
}



// WEBPACK FOOTER //
// ./src/ducks/quickmatch.js